/**
 * SPEC.md §11 — issue #37 (M6.10): the Source/Preview tab strip App.tsx
 * shows only below the 820px breakpoint, once its two-pane grid collapses
 * to one visible pane at a time. Fully controlled and stateless — App.tsx
 * owns which pane is active (shared with the global `Cmd/Ctrl+B` shortcut
 * that also toggles it) and this component only renders the two buttons
 * and reports clicks, the same "report a selection, don't own state" shape
 * every other self-contained control in this codebase follows.
 *
 * Left/Right arrow keys move focus and selection between tabs (the WAI-ARIA
 * tabs pattern's "automatic activation" model — SPEC.md §11's own
 * accessibility bar already holds Splitter.tsx's `role="separator"` to a
 * real keyboard interaction, not just click; a `role="tablist"` deserves
 * the same). Only two tabs, so wrapping Left-from-Source or
 * Right-from-Preview is the same as just toggling.
 */
export type Pane = 'source' | 'preview';

interface PaneTabsProps {
  activeTab: Pane;
  onSelect: (tab: Pane) => void;
}

const TABS: { id: Pane; label: string }[] = [
  { id: 'source', label: 'Source' },
  { id: 'preview', label: 'Preview' },
];

export default function PaneTabs({ activeTab, onSelect }: PaneTabsProps) {
  function handleKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const next: Pane = activeTab === 'source' ? 'preview' : 'source';
    onSelect(next);
    e.currentTarget.parentElement?.querySelector<HTMLButtonElement>(`#pane-tab-${next}`)?.focus();
  }

  return (
    <div role="tablist" aria-label="Pane" className="flex shrink-0 border-b border-ink-3 bg-ink-2">
      {TABS.map((tab) => (
        <button
          key={tab.id}
          id={`pane-tab-${tab.id}`}
          type="button"
          role="tab"
          aria-selected={activeTab === tab.id}
          onClick={() => onSelect(tab.id)}
          onKeyDown={handleKeyDown}
          className={`flex-1 px-3 py-2 text-xs ${
            activeTab === tab.id ? 'border-b-2 border-pink text-text-ink' : 'border-b-2 border-transparent text-mute hover:text-text-ink'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}
